// Activities API service
import { baseClient } from './baseClient';
import type {
  Activity,
  ActivityCreate,
  ActivityUpdate,
  ActivityFilters
} from '../types/api';

export class ActivitiesApi {
  private static instance: ActivitiesApi;

  private constructor() { }

  public static getInstance(): ActivitiesApi {
    if (!ActivitiesApi.instance) {
      ActivitiesApi.instance = new ActivitiesApi();
    }
    return ActivitiesApi.instance;
  }

  /**
   * Get all activities with optional filtering
   */
  async getActivities(filters?: ActivityFilters): Promise<Activity[]> {
    const params = new URLSearchParams();

    if (filters?.q) params.append('q', filters.q);
    if (filters?.skip) params.append('skip', filters.skip.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.contact_id) params.append('contact_id', filters.contact_id);
    if (filters?.activity_type) params.append('activity_type', filters.activity_type);

    const queryString = params.toString();
    const url = queryString ? `/activities?${queryString}` : '/activities';

    return baseClient.get<Activity[]>(url);
  }

  /**
   * Get a single activity by ID
   */
  async getActivity(id: string): Promise<Activity> {
    return baseClient.get<Activity>(`/activities/${id}`);
  }

  /**
   * Create a new activity
   */
  async createActivity(activity: ActivityCreate): Promise<Activity> {
    return baseClient.post<Activity>('/activities', activity);
  }

  /**
   * Update an existing activity (typically for AI analysis results)
   */
  async updateActivity(id: string, activity: ActivityUpdate): Promise<Activity> {
    return baseClient.patch<Activity>(`/activities/${id}`, activity);
  }

  /**
   * Delete an activity
   */
  async deleteActivity(id: string): Promise<void> {
    return baseClient.delete<void>(`/activities/${id}`);
  }

  /**
   * Get activities by contact
   */
  async getActivitiesByContact(contactId: string): Promise<Activity[]> {
    return this.getActivities({ contact_id: contactId });
  }

  /**
   * Get activities by type
   */
  async getActivitiesByType(activityType: string): Promise<Activity[]> {
    return this.getActivities({ activity_type: activityType });
  }

  /**
   * Get recent activities
   */
  async getRecentActivities(days: number = 7): Promise<Activity[]> {
    const activities = await this.getActivities();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    return activities.filter(activity =>
      new Date(activity.created_at) >= cutoffDate
    );
  }

  /**
   * Get activities by date range
   */
  async getActivitiesByDateRange(startDate: Date, endDate: Date): Promise<Activity[]> {
    const activities = await this.getActivities();

    return activities.filter(activity => {
      const activityDate = new Date(activity.created_at);
      return activityDate >= startDate && activityDate <= endDate;
    });
  }

  /**
   * Get email activities
   */
  async getEmailActivities(): Promise<Activity[]> {
    return this.getActivitiesByType('Email');
  }

  /**
   * Get call activities
   */
  async getCallActivities(): Promise<Activity[]> {
    return this.getActivitiesByType('Call');
  }

  /**
   * Get meeting activities
   */
  async getMeetingActivities(): Promise<Activity[]> {
    return this.getActivitiesByType('Meeting');
  }

  /**
   * Get activities with AI analysis
   */
  async getActivitiesWithAnalysis(): Promise<Activity[]> {
    const activities = await this.getActivities();
    return activities.filter(activity =>
      activity.summary || activity.sentiment || activity.detected_action_items
    );
  }

  /**
   * Get activities by sentiment
   */
  async getActivitiesBySentiment(sentiment: string): Promise<Activity[]> {
    const activities = await this.getActivities();
    return activities.filter(activity => activity.sentiment === sentiment);
  }

  /**
   * Get activities with action items
   */
  async getActivitiesWithActionItems(): Promise<Activity[]> {
    const activities = await this.getActivities();
    return activities.filter(activity =>
      activity.detected_action_items && activity.detected_action_items.length > 0
    );
  }

  /**
   * Bulk create activities (for email sync, etc.)
   */
  async bulkCreateActivities(activities: ActivityCreate[]): Promise<Activity[]> {
    const promises = activities.map(activity => this.createActivity(activity));
    return Promise.all(promises);
  }

  /**
   * Get activity statistics
   */
  async getActivityStats(contactId?: string): Promise<any> {
    const activities = contactId
      ? await this.getActivitiesByContact(contactId)
      : await this.getActivities();

    const stats = {
      total: activities.length,
      byType: activities.reduce((acc, activity) => {
        acc[activity.activity_type] = (acc[activity.activity_type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      bySentiment: activities.reduce((acc, activity) => {
        if (activity.sentiment) {
          acc[activity.sentiment] = (acc[activity.sentiment] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>),
      withActionItems: activities.filter(a =>
        a.detected_action_items && a.detected_action_items.length > 0
      ).length,
      recent: activities.filter(a => {
        const activityDate = new Date(a.created_at);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return activityDate >= weekAgo;
      }).length,
    };

    return stats;
  }
}

// Export singleton instance
export const activitiesApi = ActivitiesApi.getInstance(); 